'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useState, useEffect } from 'react';

import { useSearchParams } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import dynamic from 'next/dynamic';

import { createGenericContext } from '@contexts/generateContext';
import useBasketInformation from '@hooks/useBasketInformation';
import { callService } from '@api/remote-call';
import { paramsToObject } from '@components/views/utils';

const TimeoutModal = dynamic(() => import('@components/TimeOutModal'), {
  ssr: false,
});
interface Timer {
  id: string;
  timeRemaining: number;
  intervalId: number | null;
  expirationTimestamp: number;
}

export interface TimerContextType {
  timers: Timer[];
  addTimer: (id: string, expirationTime?: number) => void;
  removeTimer: (id: string) => void;
  subscribeTimerFinished: (
    listenerId: string,
    timerId: string,
    callback: (timerId: string) => void
  ) => void;
  unsubscribeTimerFinished: (listenerId: string) => void;
}

const [useTimer, TimerContextProvider] =
  createGenericContext<TimerContextType>();

const TimerProvider = (
  props: React.PropsWithChildren<unknown>
): React.JSX.Element => {
  const [removeItemId, setRemoveItemId] = useState('');
  const [isPackage, setIsPackage] = useState(false);
  const [isHotel, setIsHotel] = useState(false);
  const [timers, setTimers] = useState<Timer[]>([]);
  const [manuallyRemovedTimers, setManuallyRemovedTimers] = useState<string[]>(
    []
  );
  const [listeners, setListeners] = useState<{
    [key: string]: { timerId: string; callback: (timerId: string) => void };
  }>({});
  const { basketInfo, refetch } = useBasketInformation();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = paramsToObject(searchParams?.entries());
  const {
    bid = typeof window !== 'undefined' && localStorage.getItem('basketId'),
  } = query;
  const [infoModalVisible, setInfoModalVisible] = useState(false);

  useEffect(() => {
    if (basketInfo) {
      if (basketInfo.isPackageBasket) {
        const shoppingFileId =
          basketInfo?.hotels?.[0]?.akgunlerData?.shoppingFileId;
        if (shoppingFileId) {
          addExistingTimer(shoppingFileId);
        }
      } else {
        const akgunlerHotels = basketInfo?.hotels?.filter(
          (hotel) => hotel?.akgunlerData?.shoppingFileId
        );
        if (akgunlerHotels?.length > 0) {
          const shoppingFileId =
            akgunlerHotels?.[0]?.akgunlerData?.shoppingFileId;
          if (shoppingFileId) {
            addExistingTimer(shoppingFileId);
          }
        }
      }
    }
  }, [basketInfo]);

  const addExistingTimer = (shoppingFileId: string) => {
    const timerActive = timers.find((timer) => timer.id === shoppingFileId);
    if (!timerActive) {
      const existingItemRemainingTimer = JSON.parse(
        localStorage.getItem(shoppingFileId)
      );
      if (existingItemRemainingTimer) {
        setTimers((prevTimers) => [
          ...prevTimers,
          {
            id: shoppingFileId,
            timeRemaining: existingItemRemainingTimer.remainingTime,
            intervalId: null,
            expirationTimestamp: existingItemRemainingTimer.expirationTimestamp,
          },
        ]);
        const intervalId = setInterval(() => {
          calculateRemainingTime();
        }, 1000) as unknown as number;

        setTimers((prevTimers) =>
          prevTimers?.map((timer) =>
            timer.id === shoppingFileId
              ? { ...timer, intervalId: intervalId }
              : timer
          )
        );
      }
    }
  };
  const calculateRemainingTime = () => {
    const intervalCurrentTime = new Date().getTime();

    setTimers((prevTimers) =>
      prevTimers.map((timer) => {
        const stored = localStorage.getItem(timer.id);

        if (!stored) {
          return timer;
        }

        const updated = {
          ...timer,
          timeRemaining: Math.floor(
            (timer.expirationTimestamp - intervalCurrentTime) / 1000
          ),
        };

        localStorage.setItem(timer.id, JSON.stringify(updated));
        return updated;
      })
    );
  };

  const addTimer = (id: string, expirationTime = 900) => {
    const checkItem = localStorage.getItem(id);
    const currentTime = new Date().getTime();
    const expirationTimestamp = currentTime + expirationTime * 1000;

    if (!checkItem) {
      if (!timers.find((timer) => timer.id === id)) {
        setTimers((prevTimers) => [
          ...prevTimers,
          {
            id,
            timeRemaining: expirationTime,
            intervalId: null,
            expirationTimestamp: expirationTimestamp,
          },
        ]);
      }
      localStorage.setItem(
        id,
        JSON.stringify({
          id,
          timeRemaining: expirationTime,
          intervalId: null,
          expirationTimestamp: expirationTimestamp,
        })
      );

      const intervalId = setInterval(() => {
        calculateRemainingTime();
      }, 1000) as unknown as number;

      setTimers((prevTimers) =>
        prevTimers.map((timer) =>
          timer.id === id ? { ...timer, intervalId: intervalId } : timer
        )
      );
    }
  };

  const removeTimer = (id: string) => {
    setManuallyRemovedTimers((prev) => [...prev, id]);
    localStorage.removeItem(id);

    const removedTimer = timers?.find((timer) => timer.id === id);

    if (removedTimer && removedTimer.intervalId) {
      clearInterval(removedTimer.intervalId);
    }

    setTimers((prevTimers) => {
      const updatedTimers = prevTimers.filter((timer) => timer.id !== id);

      return updatedTimers;
    });
  };

  const subscribeTimerFinished = (
    listenerId: string,
    timerId: string,
    callback: (timerId: string) => void
  ) => {
    setListeners((prevListeners) => ({
      ...prevListeners,
      [listenerId]: { timerId, callback },
    }));
  };

  const unsubscribeTimerFinished = (listenerId: string) => {
    setListeners((prevListeners) => {
      const updatedListeners = { ...prevListeners };
      delete updatedListeners[listenerId];
      return updatedListeners;
    });
  };

  const { mutate: removeBasket } = useMutation(
    ({ itemId }: { itemId: string }) =>
      callService(
        { service: 'BasketsService', method: 'remove', version: 'v3' },
        {
          basketId: bid as string,
          itemId,
        }
      ),
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const { mutate: removePackage } = useMutation(
    ({ itemId }: { itemId: string }) =>
      callService(
        { service: 'BasketsService', method: 'packagesRemove', version: 'v3' },
        {
          basketId: bid as string,
          itemId,
        }
      ),
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const { mutate: unlockBasket } = useMutation(
    () =>
      callService(
        { service: 'BasketsService', method: 'unlock', version: 'v3' },
        {
          basketId: bid as string,
        }
      ),
    {
      onSuccess: () => {
        if (isHotel) {
          removeBasket({ itemId: removeItemId });
        }
        if (isPackage) {
          removePackage({ itemId: removeItemId });
        }
      },
    }
  );

  useEffect(() => {
    const finishedTimers = timers.filter((timer) => timer.timeRemaining <= 0);
    if (finishedTimers.length > 0) {
      finishedTimers.forEach((finishedTimer) => {
        clearInterval(finishedTimer.intervalId ?? 0);
        Object.values(listeners).forEach(({ timerId, callback }) => {
          if (timerId === finishedTimer.id) {
            callback(finishedTimer.id);
          }
        });

        if (!manuallyRemovedTimers.includes(finishedTimer.id)) {
          const isPackage = basketInfo?.isPackageBasket === true;
          setIsPackage(isPackage);
          setIsHotel(!isPackage);

          const expiredHotel = basketInfo?.hotels?.find(
            (hotel) => hotel.akgunlerData?.shoppingFileId === finishedTimer.id
          );

          if (expiredHotel) {
            setInfoModalVisible(true);

            const itemId = expiredHotel?.itemId ?? '';
            const isLocked = basketInfo?.isLocked;
            const isAdvancePayment = query?.isAdvancePayment;

            if (isLocked && !isAdvancePayment) {
              setRemoveItemId(itemId);
              unlockBasket();
            } else {
              isPackage ? removePackage({ itemId }) : removeBasket({ itemId });
            }
          }
        }

        setListeners((prevListeners) => {
          const updatedListeners = { ...prevListeners };
          const listenerId = `listener${finishedTimer.id}`;
          delete updatedListeners[listenerId];
          return updatedListeners;
        });
        setTimers((prevTimers) => {
          const updatedTimers = prevTimers.filter(
            (timer) => timer.id !== finishedTimer.id
          );
          localStorage.removeItem(finishedTimer.id);
          return updatedTimers;
        });

        setManuallyRemovedTimers((prev) =>
          prev.filter((id) => id !== finishedTimer.id)
        );
      });
    }
  }, [timers, listeners]);

  return (
    <TimerContextProvider
      value={{
        timers,
        addTimer,
        removeTimer,
        subscribeTimerFinished,
        unsubscribeTimerFinished,
      }}
    >
      {props.children}
      {infoModalVisible && !query.isAdvancePayment ? (
        <TimeoutModal setInfoModalVisible={setInfoModalVisible} />
      ) : null}
    </TimerContextProvider>
  );
};

export { TimerProvider, useTimer };
